import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Bold, Italic, Link2, Image as ImgIcon,
  Heading1, Heading2, List, ListOrdered, Quote,
  Undo, Redo, Loader2,
} from "lucide-react";

const Btn = ({ onClick, active, title, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    data-testid={testid}
    className={`p-1.5 rounded transition ${active ? "bg-kore-light/70 text-kore-forest" : "text-kore-ink/70 hover:bg-kore-light/30"}`}
  >
    {children}
  </button>
);

export default function RichEditor({ value = "", onChange, minHeight = 260 }) {
  const fileRef = useRef(null);
  const uploadingRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Image.configure({ inline: false, HTMLAttributes: { class: "rounded-xl my-4 w-full" } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: "text-kore-forest underline underline-offset-2" } }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none px-4 py-3 min-h-full text-kore-ink/85",
      },
    },
  });

  // Sync incoming value when parent replaces it (e.g. opening a different post)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && !editor.isFocused) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const uploadImage = async (file) => {
    if (uploadingRef.current) return;
    uploadingRef.current = true;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      editor.chain().focus().setImage({ src: data.url }).run();
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Image upload failed");
    } finally {
      uploadingRef.current = false;
    }
  };

  const addLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL (leave blank to remove)", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
  };

  return (
    <div data-testid="rich-editor" className="border border-kore-ink/15 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-1 flex-wrap border-b border-kore-ink/10 bg-kore-warm/50 px-2 py-1.5">
        <Btn testid="rte-bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold"><Bold size={14} /></Btn>
        <Btn testid="rte-italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic"><Italic size={14} /></Btn>
        <span className="w-px h-4 bg-kore-ink/15 mx-1" />
        <Btn testid="rte-h2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading"><Heading1 size={14} /></Btn>
        <Btn testid="rte-h3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Sub-heading"><Heading2 size={14} /></Btn>
        <span className="w-px h-4 bg-kore-ink/15 mx-1" />
        <Btn testid="rte-ul" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet list"><List size={14} /></Btn>
        <Btn testid="rte-ol" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered list"><ListOrdered size={14} /></Btn>
        <Btn testid="rte-quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Quote"><Quote size={14} /></Btn>
        <span className="w-px h-4 bg-kore-ink/15 mx-1" />
        <Btn testid="rte-link" onClick={addLink} active={editor.isActive("link")} title="Link"><Link2 size={14} /></Btn>
        <Btn testid="rte-image" onClick={() => fileRef.current?.click()} title="Insert image">
          {uploadingRef.current ? <Loader2 size={14} className="animate-spin" /> : <ImgIcon size={14} />}
        </Btn>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          data-testid="rte-image-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadImage(f);
            e.target.value = "";
          }}
        />
        <span className="w-px h-4 bg-kore-ink/15 mx-1" />
        <Btn testid="rte-undo" onClick={() => editor.chain().focus().undo().run()} title="Undo"><Undo size={14} /></Btn>
        <Btn testid="rte-redo" onClick={() => editor.chain().focus().redo().run()} title="Redo"><Redo size={14} /></Btn>
      </div>
      <div
        style={{ minHeight }}
        className="max-h-[60vh] overflow-y-auto"
        onClick={() => editor.chain().focus().run()}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
